import { formatRelativeDayLabel } from '../../utils/relativeDay.js';

export function nameFromEmail(email) {
    if (!email || typeof email !== 'string') return 'You';
    const local = email.split('@')[0] || '';
    const cleaned = local.replace(/[._-]+/g, ' ').trim();
    if (!cleaned) return 'You';
    return cleaned
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

export function initialsFromName(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function displayNameFor(profile, email) {
    const stored = profile?.displayName?.trim();
    if (stored) return stored;
    return nameFromEmail(email);
}

/** /auth/me includes every user event; keep identity fields only. */
export function identityFromMe(me, fallback = {}) {
    const raw = me?.userProfile || {};
    const email = raw.email || me?.user?.email || fallback.email || '';
    return {
        displayName: raw.displayName || fallback.displayName || null,
        avatarUrl: raw.avatarUrl || fallback.avatarUrl || null,
        email,
        createdAt: raw.createdAt || me?.user?.created_at || fallback.createdAt || '',
    };
}

export function identityFromAuthUser(user) {
    if (!user) return { displayName: null, avatarUrl: null, email: '', createdAt: '' };
    const meta = user.user_metadata || {};
    return {
        displayName: meta.displayName || null,
        avatarUrl: meta.avatarUrl || null,
        email: user.email || '',
        createdAt: user.created_at || '',
    };
}

export function platformLabel(source) {
    if (!source) return '';
    const value = String(source).toLowerCase();
    if (value === 'epg' || value.includes('epg') || value === 'tv') return 'TV';
    if (value.includes('youtube')) return 'YouTube';
    if (value.includes('twitch')) return 'Twitch';
    return source;
}

export function isUpcomingEventSubscription(sub) {
    const raw = sub?.event?.event_date;
    if (!raw) return false;
    const at = new Date(raw);
    if (Number.isNaN(at.getTime())) return false;
    return at.getTime() > Date.now();
}

export function subscriptionTitle(sub) {
    if (sub?.event?.event_name) return sub.event.event_name;
    if (sub?.entity?.name) return sub.entity.name;
    return 'Upcoming event';
}

export function subscriptionMeta(sub) {
    const parts = [];
    const date = sub?.event?.event_date ? new Date(sub.event.event_date) : null;
    if (date && !Number.isNaN(date.getTime())) {
        parts.push(formatWhen(date));
    }
    const source = platformLabel(sub?.event?.api_source);
    if (source) parts.push(source);
    return parts.join(' · ');
}

export function formatWhen(date) {
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${formatRelativeDayLabel(date)} ${time}`;
}

export function formatMemberSince(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString([], { month: 'short', year: 'numeric' });
}

export function prefersReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const AVATAR_MAX_SOURCE_BYTES = 8 * 1024 * 1024;
const AVATAR_ALLOWED_TYPES = /^image\/(?:jpeg|jpg|pjpeg|png|gif|webp)$/i;

export function compressAvatarFile(file) {
    return new Promise((resolve, reject) => {
        if (!file || !AVATAR_ALLOWED_TYPES.test(file.type)) {
            reject(new Error('Use a JPEG, PNG, GIF, or WebP photo'));
            return;
        }
        if (file.size > AVATAR_MAX_SOURCE_BYTES) {
            reject(new Error('Photo is too large. Use an image under 8MB.'));
            return;
        }
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            try {
                const size = 192;
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                const min = Math.min(img.width, img.height) || 1;
                const sx = (img.width - min) / 2;
                const sy = (img.height - min) / 2;
                ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
                URL.revokeObjectURL(url);
                const data = canvas.toDataURL('image/jpeg', 0.82);
                if (data.length > 180000) {
                    reject(new Error('Photo is too large. Try a smaller image.'));
                    return;
                }
                resolve(data);
            } catch (err) {
                URL.revokeObjectURL(url);
                reject(err);
            }
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Could not read that photo'));
        };
        img.src = url;
    });
}
